import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useOutletContext } from 'react-router-dom';

import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getSelectedCatalogItems,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import {
    getSelectCategoryId,
    getSelectCategoryTitle,
    getSelectSubCategoryId,
    getSelectSubCategoryName,
} from 'entities/Category/model/selectors/categorySelectors';
import { getUserCanEditPropertyCatalog } from 'entities/User/model/selectors/userPermissionsSeletors';
import {
    getSearchGlobalCatalogItemValue,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import {
    type CatalogCategoryChangeFollowUp,
    ChangeCatalogItemCategoryButton,
} from 'features/changeCatalogItemCategory';
import { MoveToInventoryCatalogItemButton } from 'features/moveToInventoryCatalogItem';
import { RemoveCatalogItem } from 'features/removeCatalogItem';
import { getRouteGlobalCatalog } from 'shared/const/router';
import { useIsOrdersAddItemsMarketplace } from 'shared/lib/contexts/CatalogIdentityContext';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { PropertyCatalogItemsList } from './PropertyCatalogItemsList';

interface PropertyCatalogItemsOutletProps {
    hasCatalogItems: boolean;
    showBulkPanel: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
}

export const PropertyCatalogItemsOutlet = () => {
    const {
        hasCatalogItems,
        showBulkPanel,
        moveTo,
        onAfterCatalogCategoryChange,
    } = useOutletContext<PropertyCatalogItemsOutletProps>();

    const selectedItems = useSelector(getSelectedCatalogItems);
    const location = useLocation();
    const catalogItems = useSelector(getCatalogItems);
    const loading = useSelector(getCatalogItemsAreLoading);
    const categoryTitle = useSelector(getSelectCategoryTitle);
    const subCategoryName = useSelector(getSelectSubCategoryName);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const selectedSubCategoryId = useSelector(getSelectSubCategoryId);
    const globalSearchValue = useSelector(getSearchGlobalCatalogItemValue);
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const canEditCatalog = useSelector(getUserCanEditPropertyCatalog);
    const isGlobalCatalogRoute = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );
    const isOrderGlobalCatalog = useIsOrdersAddItemsMarketplace();
    const isGlobalCatalogMode = Boolean(
        isGlobalCatalogRoute || isOrderGlobalCatalog,
    );
    const currentPageIsOrders = useMatch('/orders' + '/*');

    const categoryHeaderText = useMemo(() => {
        if (!categoryTitle) {
            return null;
        }

        if (isGlobalCatalogMode && subCategoryName) {
            return `${categoryTitle} / ${subCategoryName}`;
        }

        return categoryTitle;
    }, [categoryTitle, isGlobalCatalogMode, subCategoryName]);

    const isSingleItem = Object.keys(selectedItems || {})?.length === 1;
    const isOrderEditing = location.state?.selectedOrderId;

    const showBulkActionsRow =
        hasCatalogItems && !isGlobalCatalogMode && showBulkPanel;

    const scrollableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo(0, 0);
        }
    }, [
        selectedCategoryId,
        selectedSubCategoryId,
        globalSearchValue,
        localSearchValue,
    ]);

    return (
        <Box
            flex="1 1"
            p="0 16px 16px"
            display="flex"
            flexDirection="column"
            sx={{ overflowY: 'scroll' }}
            ref={scrollableRef}
        >
            {categoryHeaderText || showBulkActionsRow ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    columnGap="16px"
                    rowGap="8px"
                    flexWrap="wrap"
                    paddingTop="16px"
                    paddingBottom="16px"
                    bgcolor="background.paper"
                    zIndex="drawer"
                >
                    {categoryHeaderText ? (
                        <Typography
                            data-testclass="catalog-items-category-header"
                            typography="openSans.subtitle1Medium"
                            color="text.primary"
                            sx={{
                                flex: '1 1 auto',
                                minWidth: 0,
                                lineHeight: '36px',
                            }}
                        >
                            {categoryHeaderText}
                        </Typography>
                    ) : (
                        <Box flex="1 1 auto" />
                    )}
                    {showBulkActionsRow ? (
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            alignItems="center"
                            justifyContent="flex-end"
                            columnGap="12px"
                            rowGap="8px"
                            sx={{ flexShrink: 0 }}
                        >
                            <RemoveCatalogItem isSingleItem={isSingleItem} />
                            {canEditCatalog ? (
                                <ChangeCatalogItemCategoryButton
                                    visualVariant="propertyCatalogToolbar"
                                    selectedItems={selectedItems}
                                    onAfterCatalogCategoryChange={
                                        onAfterCatalogCategoryChange
                                    }
                                    data-testclass="property-catalog-bulk-edit-category-button"
                                />
                            ) : null}
                            <MoveToInventoryCatalogItemButton
                                isSingleItem={isSingleItem}
                            />
                        </Box>
                    ) : null}
                </Box>
            ) : null}
            <PropertyCatalogItemsList
                items={catalogItems}
                loading={loading}
                moveTo={moveTo}
                quickEdit={isOrderEditing}
                isOrderPage={!!currentPageIsOrders}
            />
        </Box>
    );
};
